import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@stores/auth.store';
import {
    getNotifications,
    getNotificationsUnreadCount,
    markNotificationRead as markReadApi,
    markAllNotificationsRead as markAllReadApi,
} from '@services/notifications.service';

export const NOTIFICATIONS_QUERY_KEY = ['notifications'];

export const NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY = [...NOTIFICATIONS_QUERY_KEY, 'unread-count'];

/** Sidebar / labels: cap display at 99+. */
export function formatNotificationUnreadLabel(count) {
    const n = Math.max(0, Math.floor(Number(count) || 0));
    if (n > 99) return '99+';
    return String(n);
}

/**
 * Notifications list + mutations. SSE is handled once app-wide by `NotificationsStreamManager`.
 */
export const useNotifications = (options = {}) => {
    const { limit = 10, page = 1 } = options;
    const queryClient = useQueryClient();
    const accessToken = useAuthStore((state) => state.accessToken);

    const hasToken = !!accessToken;

    const { data, isLoading, refetch } = useQuery({
        queryKey: [...NOTIFICATIONS_QUERY_KEY, { page, limit }],
        queryFn: () => getNotifications({ page, limit }),
        enabled: hasToken,
        retry: (failureCount, error) => {
            if (error?.response?.status === 401) return false;
            return failureCount < 1;
        },
    });

    const unreadCountQuery = useQuery({
        queryKey: NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY,
        queryFn: getNotificationsUnreadCount,
        enabled: hasToken,
        retry: (failureCount, error) => {
            if (error?.response?.status === 401) return false;
            return failureCount < 1;
        },
    });

    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const listUnreadCount = items.filter((n) => !n.read).length;
    const unreadCount =
        !unreadCountQuery.isError &&
        typeof unreadCountQuery.data === 'number' &&
        unreadCountQuery.data >= 0
            ? unreadCountQuery.data
            : listUnreadCount;

    const markReadMutation = useMutation({
        mutationFn: (id) => markReadApi(id),
        onMutate: async (id) => {
            const now = new Date().toISOString();
            queryClient.setQueriesData({ queryKey: NOTIFICATIONS_QUERY_KEY }, (old) => {
                if (!old?.items) return old;
                return {
                    ...old,
                    items: old.items.map((n) => (n.id === id ? { ...n, read: true, readAt: now } : n)),
                };
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
        },
    });

    const markAllReadMutation = useMutation({
        mutationFn: () => markAllReadApi(),
        onMutate: async () => {
            const now = new Date().toISOString();
            queryClient.setQueriesData({ queryKey: NOTIFICATIONS_QUERY_KEY }, (old) => {
                if (!old?.items) return old;
                return {
                    ...old,
                    items: old.items.map((n) => ({ ...n, read: true, readAt: now })),
                };
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
        },
    });

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleString();
    };

    return {
        items,
        total,
        unreadCount,
        isLoading,
        refetch,
        markRead: markReadMutation.mutate,
        markReadPending: markReadMutation.isPending,
        markAllRead: markAllReadMutation.mutate,
        markAllReadPending: markAllReadMutation.isPending,
        formatDate,
    };
};
